import { useCallback, useState } from 'react';
import { useToast } from '@material-bank/mb-uikit/shared/stores';
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

export const useCreateQueryClient = () => {
  const { showErrorToast } = useToast();

  const showQueryError = useCallback(
    (error: unknown) => {
      const errorMessage =
        (error instanceof AxiosError && error.response?.data?.message) ||
        (error instanceof Error && error.message) ||
        undefined;

      showErrorToast({ collapsibleContent: errorMessage });
    },
    [showErrorToast],
  );

  const handleError = useCallback(
    (error: unknown, options?: { suppressErrorToast?: unknown }) => {
      const { suppressErrorToast } = options ?? {};

      if (!suppressErrorToast) {
        showQueryError(error);
        return;
      }

      if (typeof suppressErrorToast === 'function') {
        if (!suppressErrorToast(error)) {
          showQueryError(error);
          return;
        }
      }
    },
    [showQueryError],
  );

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            const { suppressErrorToast } = query.meta ?? {};

            handleError(error, { suppressErrorToast });
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            const { suppressErrorToast } = mutation.meta ?? {};

            handleError(error, { suppressErrorToast });
          },
        }),
      }),
  );

  return queryClient;
};

const axiosRequestConfig: AxiosRequestConfig = {
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
};

export const apiClient: AxiosInstance = axios.create(axiosRequestConfig);
export const publicApiClient: AxiosInstance = axios.create();
